/**
 * §12.4 — a brand's own colour, chosen.
 *
 * `null` is a real choice here ("Standard colour": the console's own hue,
 * §3's table), not the absence of one — so the radio group below is
 * `[Standard, ...ACCENT_PRESETS]`, nine mutually exclusive items, with the
 * same roving-tabindex WAI-ARIA radio pattern `ThemeToggle` uses in
 * shell.tsx (one tab stop for the group, arrows move the choice and the
 * focus together). A custom colour sits apart from that group: it is not one
 * of a fixed set of options, so it is a native swatch plus a hex field
 * instead of a tenth radio button standing in for "something else".
 *
 * The preview shows both themes: the colour is drawn lighter on the dark
 * theme (or darker on the light) where its text would not be readable, and
 * a preview of only the reader's own theme looked like a mis-pick.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { ACCENT_PRESETS, accentStateClash, accentTokens, isAccentHex } from './accent.ts';
import { kitText } from './messages.ts';
import { Callout, Field, TextInput } from './ui.tsx';

interface Choice {
  readonly hex: string | null;
  readonly name: string;
}

export function AccentPicker({
  value,
  onChange,
  disabled,
  id,
  label,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  disabled?: boolean;
  id?: string;
  label?: string;
}) {
  const groupLabel = label ?? kitText('accent.picker.group');
  const choices = useMemo<Choice[]>(
    () => [
      { hex: null, name: kitText('accent.picker.standard') },
      ...ACCENT_PRESETS.map((preset) => ({ hex: preset.hex, name: kitText(preset.nameKey) })),
    ],
    [],
  );
  const normalized = value ? value.toLowerCase() : null;
  const selectedIndex = choices.findIndex((c) => c.hex === normalized);
  // Nothing picked from the row (a custom hex that is not one of the eight,
  // or an invalid draft): the group still needs exactly one tab stop, so
  // focus lands on "Standard colour" rather than on nothing.
  const focusIndex = selectedIndex === -1 ? 0 : selectedIndex;
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  /*
   * The hex field is typed freehand ("#ad2" is not yet a colour), so it
   * needs a draft that can sit invalid without the controlled `value`
   * snapping it back mid-keystroke — resynced whenever `value` changes for a
   * reason *other* than this draft (a preset click, a caller resetting the
   * field), which is exactly what "value changed" means from here.
   */
  const [hexDraft, setHexDraft] = useState(value ?? '');
  useEffect(() => {
    setHexDraft(value ?? '');
  }, [value]);
  const hexInvalid = hexDraft.length > 0 && !isAccentHex(hexDraft);

  const tokens = value && isAccentHex(value) ? accentTokens(value) : null;
  // A colour in force that none of the swatches holds: the custom well is
  // the reader's choice, and is ringed as such.
  const custom = normalized !== null && selectedIndex === -1 && isAccentHex(normalized);
  const clash = value && isAccentHex(value) ? accentStateClash(value) : null;

  function choose(hex: string | null) {
    onChange(hex);
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (index + 1) % choices.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (index - 1 + choices.length) % choices.length;
    else return;
    e.preventDefault();
    choose(choices[next]!.hex);
    buttons.current[next]?.focus();
  }

  function onHexChange(text: string) {
    setHexDraft(text);
    if (isAccentHex(text)) choose(text.toLowerCase());
  }

  return (
    <fieldset id={id} className="bare-fieldset accent-picker" disabled={disabled}>
      <div className="accent-swatches" role="radiogroup" aria-label={groupLabel}>
        {choices.map((choice, index) => {
          const checked = index === selectedIndex;
          return (
            <button
              key={choice.hex ?? 'standard'}
              ref={(el) => { buttons.current[index] = el; }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={choice.name}
              title={choice.name}
              tabIndex={index === focusIndex ? 0 : -1}
              className={choice.hex ? 'accent-swatch' : 'accent-swatch is-standard'}
              style={choice.hex ? ({ background: choice.hex } as CSSProperties) : undefined}
              onClick={() => choose(choice.hex)}
              onKeyDown={(e) => onKeyDown(e, index)}
            />
          );
        })}
      </div>

      {/* A custom colour: the native well (it opens the system picker) and
          the hex box beside it, in one compact row. Ringed like a swatch
          when the colour in force is not one of the presets above, so the
          reader can always see which of the two rows holds their choice. */}
      <div className="accent-custom-row">
        <label className={custom ? 'accent-custom is-picked' : 'accent-custom'}>
          <input
            type="color"
            className="accent-custom-swatch"
            aria-label={kitText('accent.picker.custom.swatch')}
            value={value && isAccentHex(value) ? value.toLowerCase() : '#808080'}
            onChange={(e) => onHexChange(e.target.value)}
          />
          <span>{kitText('accent.picker.custom')}</span>
        </label>
        <div className="accent-hex-field">
          <Field
            label={kitText('accent.picker.hex')}
            hint={kitText('accent.picker.hex.hint')}
            {...(hexInvalid ? { error: kitText('accent.picker.hex.invalid') } : {})}
          >
            <TextInput
              value={hexDraft}
              onChange={onHexChange}
              placeholder="#ad2b7c"
              invalid={hexInvalid}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </Field>
        </div>
      </div>

      {tokens ? (
        /* Both themes side by side, each on its own theme's surface, because
           staff see whichever theme they run: a dark colour has to be drawn
           lighter on the dark theme (and a light one darker on the light
           theme) for its text to stay readable (§7), and showing only the
           reader's own theme made the other one look like the wrong colour
           had been picked. Each half says when it was adjusted. */
        <div
          className="accent-preview-pair"
          style={{
            '--a': tokens.light.a,
            '--a-ink': tokens.light.aInk,
            '--a-wash': tokens.light.aWash,
            '--ad': tokens.dark.ad,
            '--ad-ink': tokens.dark.adInk,
            '--ad-wash': tokens.dark.adWash,
          } as CSSProperties}
        >
          {([
            ['is-light', 'accent.picker.preview.light', tokens.adjusted, tokens.light.a],
            ['is-dark', 'accent.picker.preview.dark', tokens.adjustedDark, tokens.dark.ad],
          ] as const).map(([theme, nameKey, adjusted, shown]) => (
            <div key={theme} className={`accent-preview ${theme}`}>
              <span className="accent-preview-theme">{kitText(nameKey)}</span>
              <div className="accent-preview-marks">
                <span className="accent-preview-chip accent-preview-action">
                  {kitText('accent.picker.preview.action')}
                </span>
                <span className="chip accent-preview-chip accent-preview-provisional">
                  {kitText('accent.picker.preview.provisional')}
                </span>
              </div>
              <span className="accent-preview-hex">
                {adjusted ? kitText('accent.picker.adjustedTo', { hex: shown }) : shown}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {clash ? <Callout tone="caution">{kitText('accent.picker.clash')}</Callout> : null}
    </fieldset>
  );
}
